import { useState } from "react";
import { Check } from "lucide-react";
import emailConfirmationIllustration from "@/assets/froppio-email-confirmation.svg";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { Brand } from "@/shared/components/ui/Brand";
import { Card } from "@/shared/components/ui/Card";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";

export function SignupConfirmationPage() {
  const { session, loading } = useAuth();
  const [linkError] = useState(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const query = new URLSearchParams(window.location.search);
    return hash.has("error") || hash.has("error_code") || query.has("error") || query.has("error_code");
  });
  const confirmed = !linkError && !!session;

  return (
    <Page>
      <PageContent className="flex max-w-none flex-col px-4 py-6 [scrollbar-gutter:auto] sm:px-5 sm:py-8">
        <Card
          role="region"
          aria-labelledby="confirmation-title"
          className="m-auto grid w-full max-w-[440px] shrink-0 grid-cols-1 overflow-hidden rounded-[28px] p-0 text-center shadow-sm sm:max-w-[840px] sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] sm:p-0 sm:text-left"
        >
          <div className="flex min-w-0 flex-col items-center justify-center bg-accent px-6 py-6 text-center sm:py-9">
            <Brand wordmark />
            <img
              src={emailConfirmationIllustration}
              width={280}
              height={240}
              alt="Froppio holding an envelope"
              className="mt-4 h-auto w-[170px] max-w-full sm:mt-8 sm:mb-6 sm:w-[260px]"
            />
            <p className="hidden text-sm leading-relaxed text-primary sm:block">
              A little less admin.<br />A little more possibility.
            </p>
          </div>
          <div className="min-w-0 self-center px-6 py-7 sm:px-10 sm:py-12">
            <header aria-live="polite">
              {!loading && confirmed && (
                <p className="mb-4 inline-flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 py-1.5 text-xs font-bold text-secondary-foreground sm:mb-[22px]">
                  <Check className="size-4" aria-hidden="true" />
                  Email verified
                </p>
              )}
              <h1 id="confirmation-title" className="text-[28px] leading-tight font-extrabold tracking-tight sm:text-[34px]">
                {loading ? "Confirming your email" : confirmed ? "Email confirmed" : "Unable to confirm email"}
              </h1>
              {!loading && confirmed && (
                <p className="mt-2.5 text-base leading-relaxed">Your next chapter starts in the app.</p>
              )}
            </header>
            <div className="mt-[18px]">
              {loading ? (
                <LoadingSpinner message="Checking your confirmation..." />
              ) : confirmed ? (
                <p className="text-sm leading-7 text-muted-foreground">
                  You can now return to the Froppio app and sign in to start setting up your business.
                </p>
              ) : (
                <ErrorState message="This link may have expired or already been used. Return to the Froppio app to try signing in, or request a new confirmation link." />
              )}
            </div>
            {!loading && (
              <footer className="mt-6 border-t border-border pt-[18px] text-xs leading-relaxed text-muted-foreground sm:mt-8 sm:pt-6">
                You can safely close this tab.
              </footer>
            )}
          </div>
        </Card>
      </PageContent>
    </Page>
  );
}
